import React, { useState } from "react";
import styled from "styled-components/macro";
import { Box, Flex } from "rebass";
import PropTypes from "prop-types";
import { Accordion } from "./_/Accordion";
import {
  COMPLEX,
  COMPLEX_TIMERANGE,
  MAJOR,
  MAJOR_TIMERANGE,
  MINOR,
  MINOR_TIMERANGE,
  MODERATE,
  MODERATE_TIMERANGE,
} from "../PreliminaryEtrType";
import { Button } from "../Button";
import { trackEvent } from "../../common/analytics";

const ContentBody = styled(Flex)`
  width: 100vw;
  height: 100%;
  max-height: 100vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: auto;

  @media (min-width: 960px) {
    max-width: 960px;
  }

  .accordion-header-one-line {
    display: block;
  }

  .accordion-header-two-lines {
    display: none;
  }

  @media (max-aspect-ratio: 1 / 2) {
    .accordion-header-one-line {
      display: none;
    }

    .accordion-header-two-lines {
      display: block;
    }
  }
`;

const GA_CATEGORY = "Outage Category Modal";

const AccordionMainHeader = styled.div`
  padding: 16px 0 24px 0;
  justify-content: space-between;
  font-weight: 700;
  font-size: ${props => props.theme.fontSizes[3]}px;
`;

export const PreliminaryEtrOutageClassification = ({
  close,
  defaultOpenCategory,
}) => {
  const handleAnalytics = args => {
    trackEvent({ category: GA_CATEGORY, ...args });
  };

  const categories = [
    {
      id: MINOR,
      title: MINOR,
      timeRange: MINOR_TIMERANGE,
      content: (
        <>
          <p>
            <strong>75%</strong> of outages are fixed with a simple repair,
            usually <strong>within 4 hours.</strong>
          </p>
          <br />
          <p>Common causes:</p>
          <ul>
            <li>Wildlife (birds, possums)</li>
            <li>A single property losing power</li>
            <li>Fallen trees or vegetation on power lines</li>
          </ul>
          <br />
          <p>
            Restoration may take longer if there are multiple outages nearby,
            heavy traffic, or more complex repairs are needed.
          </p>
          <br />
        </>
      ),
    },
    {
      id: MODERATE,
      title: MODERATE,
      timeRange: MODERATE_TIMERANGE,
      content: (
        <>
          <p>
            Around <strong>10%</strong> of outages take up to{" "}
            <strong>8 hours</strong> to restore because they need more
            complicated repairs.
          </p>
          <br />
          <p>Common causes:</p>
          <ul>
            <li>Damage to overhead power lines or equipment</li>
            <li>Damaged power poles</li>
            <li>Road access blocked for crews (e.g tree or landslip)</li>
            <li>Fallen trees or vegetation on power lines</li>
          </ul>
          <br />
          <p>
            Sometimes we need to temporarily turn off power in the area to keep
            crews and the community safe.
          </p>
        </>
      ),
    },
    {
      id: MAJOR,
      title: MAJOR,
      timeRange: MAJOR_TIMERANGE,
      content: (
        <>
          <p>
            About <strong>5%</strong> of outages take up to{" "}
            <strong>12 hours</strong> to fix because they involve complex
            technical repairs.
          </p>
          <br />
          <p>Common causes:</p>
          <ul>
            <li>Underground cables that require excavation</li>
            <li>
              Severe storm damage needing debris removal or coordination with
              emergency crews
            </li>
            <li>Traffic accidents that damage power lines or equipment.</li>
          </ul>
          <br />
        </>
      ),
    },
    {
      id: COMPLEX,
      title: COMPLEX,
      timeRange: COMPLEX_TIMERANGE,
      content: (
        <>
          <p>
            In rare cases, outages take <strong>more than 12 hours</strong> to
            restore due to severe damage or hard-to-reach locations.
          </p>
          <br />
          <p>Possible causes:</p>
          <ul>
            <li>Extreme weather events</li>
            <li>Remote or inaccessible locations</li>
            <li>Major network or equipment damage</li>
          </ul>
        </>
      ),
    },
  ];

  const [activeAccordian, setActiveAccordian] = useState(
    categories.some(category => category.id === defaultOpenCategory)
      ? defaultOpenCategory
      : MINOR,
  );

  return (
    <ContentBody
      bg="white"
      color="text"
      flexDirection="column"
      width={1}
      py={[30, null, 50]}
      px={[20, null, 50]}
    >
      <AccordionMainHeader className="accordion-header-one-line">
        Outage causes & Fix times
      </AccordionMainHeader>
      <AccordionMainHeader className="accordion-header-two-lines">
        Outage causes & <br />
        Fix times
      </AccordionMainHeader>
      <div>
        {categories.map(category => (
          <Accordion
            key={category.id}
            id={category.id}
            title={category.title}
            timeRange={category.timeRange}
            isOpen={activeAccordian === category.id}
            onClick={setActiveAccordian}
            content={category.content}
          />
        ))}
      </div>
      <Box width={1} alignSelf="center" py="5px">
        <Button
          variant="ghost"
          block
          onClick={close}
          gaTrack={() =>
            handleAnalytics({
              action: "Clicked Close Button on Outage Category Popup",
            })
          }
        >
          close
        </Button>
      </Box>
    </ContentBody>
  );
};

PreliminaryEtrOutageClassification.propTypes = {
  close: PropTypes.func.isRequired,
  defaultOpenCategory: PropTypes.oneOf([MINOR, MODERATE, MAJOR, COMPLEX]),
};

PreliminaryEtrOutageClassification.defaultProps = {
  defaultOpenCategory: MINOR,
};
